class CartDiscount extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('#cart-discount-form');
    this.input = this.querySelector('#discount-code-input');
    this.submitButton = this.querySelector('.discount-form__button');
    this.successMessage = this.querySelector('#discount-code-success');
    this.errorMessage = this.querySelector('#discount-code-error');
    this.miniCart = document.querySelector('mini-cart');

    this.init();
  }

  init() {
    if (this.form) {
      // Prevent default form submission
      this.form.addEventListener('submit', this.handleSubmit.bind(this));
    }

    // Add click handler to the submit button
    if (this.submitButton) {
      this.submitButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleSubmit(e);
      });
    }

    // Add Enter key handler to the input field
    if (this.input) {
      this.input.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
          this.handleSubmit(e);
        }
      });
    }

    // Listen for cart updates to clear messages when cart changes
    document.addEventListener('cart:updated', this.handleCartUpdate.bind(this));
  }

  handleCartUpdate() {
    // Clear any existing messages when cart is updated
    this.clearMessages();
  }

  async handleSubmit(event) {
    // Always prevent default form submission
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const discountCode = this.input.value.trim();
    if (!discountCode) {
      this.showError('Please enter a discount code');
      return;
    }

    // Prevent multiple simultaneous requests
    if (this.submitButton && this.submitButton.disabled) {
      return;
    }

    this.setLoading(true);
    this.clearMessages();

    try {
      console.log('Applying discount code:', discountCode);
      const parsedState = await this.applyDiscount(discountCode);
      console.log('Discount application response:', parsedState);

      // If we get here, the discount was successfully applied
      await this.updateCartDrawer(parsedState);

      // Show success message after cart update is complete
      this.showSuccess('Discount code applied successfully!');
      this.input.value = '';
    } catch (error) {
      console.error('Discount application failed:', error);
      // For errors, show immediately since cart won't update
      this.showError(error.message || 'Failed to apply discount code. Please try again.');
    } finally {
      this.setLoading(false);
    }
  }

  async applyDiscount(code) {
    // Get the sections that need to be updated
    const sections = this.miniCart ? this.miniCart.getSectionsToRender().map((section) => section.section) : [];

    // Use the standard Shopify discount code application method with sections
    const formData = new FormData();
    formData.append('discount', code);
    sections.forEach((section) => {
      formData.append('sections[]', section);
    });
    formData.append('sections_url', window.location.pathname);

    const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Discount application failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();
    console.log('Full cart response:', result);

    // Check for explicit errors first
    if (result.errors && result.errors.discount) {
      throw new Error(result.errors.discount);
    }

    // Check if the discount was actually applied by looking for discount applications
    if (result.cart_level_discount_applications && result.cart_level_discount_applications.length > 0) {
      console.log('Discount applications found:', result.cart_level_discount_applications);
      return { ...result, discount_applied: true };
    }

    // Check if total_discount increased (another way to detect successful application)
    if (result.total_discount && result.total_discount > 0) {
      console.log('Discount detected via total_discount:', result.total_discount);
      return { ...result, discount_applied: true };
    }

    // Sometimes Shopify applies the discount but doesn't immediately reflect it in the response
    // Let's wait a moment and check the cart again
    console.log('No immediate discount detected, checking cart again...');
    await new Promise((resolve) => setTimeout(resolve, 500));

    try {
      const updatedCart = await this.getUpdatedCart();
      console.log('Updated cart after delay:', updatedCart);

      if (updatedCart.cart_level_discount_applications && updatedCart.cart_level_discount_applications.length > 0) {
        console.log('Discount found in delayed check:', updatedCart.cart_level_discount_applications);
        return { ...updatedCart, discount_applied: true };
      }

      if (updatedCart.total_discount && updatedCart.total_discount > 0) {
        console.log('Discount detected via total_discount in delayed check:', updatedCart.total_discount);
        return { ...updatedCart, discount_applied: true };
      }
    } catch (error) {
      console.log('Error checking updated cart:', error);
    }

    // If we get here, the discount wasn't applied
    throw new Error('Invalid discount code. Please check the code and try again.');
  }

  async getUpdatedCart() {
    const response = await fetch(window.Shopify.routes.root + 'cart.js', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to get updated cart');
    }

    return await response.json();
  }

  async updateCartDrawer(parsedState) {
    try {
      // Update the mini cart with the new state
      if (this.miniCart && parsedState) {
        this.miniCart.renderContents(parsedState);
      }

      // Dispatch custom event to notify other components
      this.dispatchEvent(
        new CustomEvent('cart:updated', {
          detail: { cart: parsedState },
          bubbles: true,
        })
      );

      // Return a promise that resolves when the DOM is updated
      return new Promise((resolve) => {
        // Use requestAnimationFrame to ensure DOM updates are complete
        requestAnimationFrame(() => {
          requestAnimationFrame(resolve);
        });
      });
    } catch (error) {
      console.error('Failed to update cart drawer:', error);
      // Even if drawer update fails, the discount was applied successfully
    }
  }

  setLoading(loading) {
    if (this.submitButton) {
      this.submitButton.disabled = loading;
      if (loading) {
        this.submitButton.classList.add('discount-form__button--loading');
        this.submitButton.textContent = 'Applying...';
      } else {
        this.submitButton.classList.remove('discount-form__button--loading');
        this.submitButton.textContent = 'Apply';
      }
    }

    if (this.input) {
      this.input.disabled = loading;
    }
  }

  showSuccess(message) {
    if (this.successMessage) {
      this.successMessage.textContent = message;
      this.successMessage.classList.add('discount-form__message--visible');
    }

    if (this.errorMessage) {
      this.errorMessage.classList.remove('discount-form__message--visible');
    }
  }

  showError(message) {
    if (this.errorMessage) {
      this.errorMessage.textContent = message;
      this.errorMessage.classList.add('discount-form__message--visible');
    }

    if (this.successMessage) {
      this.successMessage.classList.remove('discount-form__message--visible');
    }
  }

  clearMessages() {
    if (this.successMessage) {
      this.successMessage.textContent = '';
      this.successMessage.classList.remove('discount-form__message--visible');
    }

    if (this.errorMessage) {
      this.errorMessage.textContent = '';
      this.errorMessage.classList.remove('discount-form__message--visible');
    }
  }

  // Method to remove discount code (can be called externally if needed)
  async removeDiscount() {
    this.setLoading(true);
    this.clearMessages();

    try {
      const sections = this.miniCart ? this.miniCart.getSectionsToRender().map((section) => section.id) : [];

      const body = JSON.stringify({
        discount: '',
        sections: sections,
        sections_url: window.location.pathname,
      });

      const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: body,
      });

      if (!response.ok) {
        throw new Error('Failed to remove discount code');
      }

      const parsedState = await response.json();
      this.showSuccess('Discount code removed successfully!');
      await this.updateCartDrawer(parsedState);
    } catch (error) {
      console.error('Discount removal failed:', error);
      this.showError('Failed to remove discount code. Please try again.');
    } finally {
      this.setLoading(false);
    }
  }
}

// Register the custom element
customElements.define('cart-discount', CartDiscount);
